import React from "react";
import { Trans } from "react-i18next";

/**
 * Helper function to extract text content from React.ReactNode
 * Handles mixed content with text strings, numbers, and React elements
 */
export const extractTextFromChildren = (children: React.ReactNode): string => {
  if (children === null || children === undefined) {
    return "";
  }

  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("");
  }

  // Handle React elements - recursively extract text from their children
  if (typeof children === "object" && children !== null) {
    // Check if it's a React element (has $$typeof symbol or type property)
    const reactElement = children as React.ReactElement;
    if (reactElement.props?.children !== undefined) {
      return extractTextFromChildren(reactElement.props.children);
    }
  }

  return "";
};

/**
 * Helper function to detect if React.ReactNode contains Korean characters
 * Korean characters (Hangul) are in the Unicode ranges:
 * - Hangul Jamo: U+1100–U+11FF
 * - Hangul Compatibility Jamo: U+3130–U+318F
 * - Hangul Syllables: U+AC00–U+D7AF
 * - Hangul Jamo Extended-A: U+A960–U+A97F
 * - Hangul Jamo Extended-B: U+D7B0–U+D7FF
 */

export const containsKoreanCharacters = (
  children: React.ReactNode
): boolean => {
  const text = extractTextFromChildren(children);

  if (!text) return false;

  // Regular expression to match Korean characters
  // Returns true if ANY Korean character is found, even when mixed with English/other text
  const koreanRegex =
    /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\uA960-\uA97F\uD7B0-\uD7FF]/;
  return koreanRegex.test(text);
};

export const containsTrans = (children: React.ReactNode): boolean => {
  // Handle null/undefined
  if (children === null || children === undefined) return false;

  // Handle strings and numbers
  if (typeof children !== "object") return false;

  // Helper to check if an element has i18n-trans className
  const hasI18nTransClassName = (props: any): boolean => {
    if (!props) return false;
    const className = props.className;
    return typeof className === "string" && className.includes("i18n-trans");
  };

  // Handle arrays (multiple children) - only check one level deep for performance
  if (Array.isArray(children)) {
    return children.some((child) => {
      if (!React.isValidElement(child)) return false;
      // Check if it's a Trans component or has i18n-trans className
      if (child.type === Trans) return true;
      return hasI18nTransClassName(child.props);
    });
  }

  // Handle single React element
  if (React.isValidElement(children)) {
    // Check if it's a Trans component
    if (children.type === Trans) return true;
    // Check if it has i18n-trans className
    return hasI18nTransClassName(children.props);
  }

  return false;
};

export const handlingDynamicFontBasedOnLanguage = (
  currentLanguage: string,
  children: React.ReactNode
): string => {
  const hasTrans = containsTrans(children);
  const isKoreanWithTrans = hasTrans && currentLanguage === "ko";

  if (
    !containsKoreanCharacters(children) &&
    currentLanguage === "ko" &&
    !isKoreanWithTrans
  )
    return "non-kr-character";

  if (
    (containsKoreanCharacters(children) && currentLanguage !== "ko") ||
    isKoreanWithTrans
  )
    return "kr-character";


    // add another condition here for future implem and handle the loading of Head and Body font in the Typography.scss
  return "";
};
